import '@modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'

import { getSessionToken } from '@common/helpers'

import type {
  WorkspaceNotificationChannel,
  WorkspaceNotificationGroupId,
  WorkspaceNotificationId,
} from '../../settings.interface'
import { WORKSPACE_NOTIFICATIONS_MOCK } from '../../settings-mocks.const'

import { useWorkspaceStore } from './workspace.store'
import { updateGroupChannels, updateNotificationChannel } from './workspace.utils'

export async function getWorkspaceNotifications(): Promise<void> {
  const { workspace, startRequest, setState, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) return

  startRequest()

  try {
    const token = await getSessionToken()
    // @ts-expect-error - method pending SDK release
    const notifications = await WorkspaceService.getWorkspaceNotifications(token, workspace.id)
    setState({ notifications: notifications ?? structuredClone(WORKSPACE_NOTIFICATIONS_MOCK) })
  } catch {
    setState({ notifications: structuredClone(WORKSPACE_NOTIFICATIONS_MOCK) })
  } finally {
    finishRequest()
  }
}

export async function updateWorkspaceNotificationChannel(
  notificationId: WorkspaceNotificationId,
  channel: WorkspaceNotificationChannel,
  enabled: boolean
): Promise<void> {
  const { workspace, updateState, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) return

  updateState((state) => {
    state.loading = true
    state.error = null
    updateNotificationChannel(state.notifications, notificationId, channel, enabled)
  })

  try {
    const token = await getSessionToken()
    // @ts-expect-error - method pending SDK release
    await WorkspaceService.updateWorkspaceNotificationChannel(token, workspace.id, notificationId, channel, enabled)
  } catch (err) {
    updateState((state) => {
      updateNotificationChannel(state.notifications, notificationId, channel, !enabled)
      state.error = err instanceof Error ? err.message : 'Failed to update workspace notification'
    })
  } finally {
    finishRequest()
  }
}

export async function updateWorkspaceNotificationGroup(
  groupId: WorkspaceNotificationGroupId,
  enabled: boolean,
  channel?: WorkspaceNotificationChannel
): Promise<void> {
  const { workspace, updateState, finishRequest } = useWorkspaceStore.getState()
  if (!workspace) return

  updateState((state) => {
    state.loading = true
    state.error = null
    updateGroupChannels(state.notifications, groupId, enabled, channel)
  })

  try {
    const token = await getSessionToken()
    // @ts-expect-error - method pending SDK release
    await WorkspaceService.updateWorkspaceNotificationGroup(token, workspace.id, groupId, enabled, channel)
  } catch (err) {
    updateState((state) => {
      updateGroupChannels(state.notifications, groupId, !enabled, channel)
      state.error = err instanceof Error ? err.message : 'Failed to update workspace notification group'
    })
  } finally {
    finishRequest()
  }
}
